import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import authService from "../../services/auth.service";
import useReveal from "../../components/useReveal";
import { GoogleIcon, EyeIcon, EyeOffIcon } from "../../components/auth/AuthIcons";

export default function LoginPageView() {
  useReveal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get("email") || "";
  const [form, setForm] = useState({ email: prefillEmail, password: "" });
  const [error, setError] = useState("");
  const fromSignup = !!prefillEmail;
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetEmail, setResetEmail] = useState(prefillEmail);
  const [resendCooldown, setResendCooldown] = useState(0);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  /* ── Email / password sign-in ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await authService.signInEmail({ email: form.email, password: form.password, rememberMe });
      navigate("/dashboard");
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot password ── */
  const startResendCooldown = () => {
    setResendCooldown(60);
    const iv = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(iv); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError("");
    if (!resetEmail) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await authService.sendPasswordReset(resetEmail);
      setResetSent(true);
      startResendCooldown();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await authService.sendPasswordReset(resetEmail);
      startResendCooldown();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Google sign-in ── */
  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await authService.signInGoogle();
      navigate("/dashboard");
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    background: "var(--bg-surface)",
    border: "1px solid var(--border-light)",
    borderRadius: 12,
    color: "var(--text-primary)",
    fontSize: 15,
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color .2s",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-secondary)",
    marginBottom: 8,
    letterSpacing: "-.01em",
  };

  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 80, minHeight: "100vh" }}>
      <section className="section section-dark">
        <div style={{ maxWidth: 440, margin: "0 auto", padding: "0 2rem" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="reveal eyebrow">Ateno account</div>
            <h1 className="reveal headline" style={{ fontSize: "clamp(32px,5vw,48px)", marginBottom: 12 }}>Sign in</h1>
            <p className="reveal subhead" style={{ fontSize: 16 }}>
              Sign in to your Ateno account.
            </p>
          </div>

          <div className="reveal" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* ── "from signup" nudge ── */}
            {fromSignup && (
              <div style={{
                background: "rgba(167,139,250,.1)", border: "1px solid rgba(167,139,250,.25)",
                borderRadius: 12, padding: "12px 16px", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5,
              }}>
                An account already exists for <strong style={{ color: "var(--text-primary)" }}>{prefillEmail}</strong>.
                Enter your password to access your dashboard.
              </div>
            )}
            {/* ── Google sign-in (hidden in forgot mode) ── */}
            {!forgotMode && (
              <button
                type="button"
                onClick={handleGoogle}
                disabled={loading}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  width: "100%", padding: "12px 16px",
                  background: "#fff", color: "#1f1f1f", fontWeight: 600, fontSize: 15, fontFamily: "inherit",
                  border: "1px solid var(--border-color)", borderRadius: 12, cursor: "pointer", transition: "opacity .2s",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <GoogleIcon /> Continue with Google
              </button>
            )}

            {/* ── Divider (hidden in forgot mode) ── */}
            {!forgotMode && (
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1, height: 1, background: "var(--border-light)" }} />
                <span style={{ fontSize: 12, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--border-light)" }} />
              </div>
            )}

            {/* ── Email / password form ── */}
            {forgotMode ? (
              /* ── Forgot password view ── */
              <form onSubmit={handleForgot} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {resetSent ? (
                  <div style={{
                    background: "var(--bg-surface)", border: "1px solid var(--border-light)",
                    borderRadius: 12, padding: "18px 20px", fontSize: 14, color: "var(--text-primary)", lineHeight: 1.7,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Check your inbox</div>
                    <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                      We sent a reset link to <strong style={{ color: "var(--text-primary)" }}>{resetEmail}</strong>.
                      It may take a minute to arrive.
                    </div>
                    <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-tertiary)" }}>
                      Didn't receive it?{" "}
                      {resendCooldown > 0 ? (
                        <span style={{ color: "var(--text-dim)" }}>Resend in {resendCooldown}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResend}
                          disabled={loading}
                          style={{ background: "none", border: "none", outline: "none", cursor: "pointer", color: "#818cf8", fontSize: 13, fontFamily: "inherit", padding: 0, opacity: loading ? 0.6 : 1 }}
                        >
                          {loading ? "Sending…" : "Resend email"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={labelStyle}>Email address</label>
                      <input
                        type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="jane@company.com" style={inputStyle}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                        onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")}
                        autoFocus
                      />
                    </div>
                    {error && (
                      <div style={{ background: "rgba(248,113,113,.1)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#f87171" }}>
                        {error}
                      </div>
                    )}
                    <button type="submit" className="btn btn-white btn-lg btn-full" disabled={loading}
                      style={{ marginTop: 4, opacity: loading ? 0.6 : 1 }}>
                      {loading ? "Sending…" : "Send reset link"}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setForgotMode(false); setResetSent(false); setError(""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#818cf8", fontSize: 13, fontFamily: "inherit", textAlign: "center" }}
                >
                  ← Back to sign in
                </button>
              </form>
            ) : (
              /* ── Sign in form ── */
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <label style={labelStyle}>Email address</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                    onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")} />
                </div>
                <div>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPwd ? "text" : "password"}
                      value={form.password}
                      onChange={set("password")}
                      placeholder="Your password"
                      style={{ ...inputStyle, paddingRight: "40px" }}
                      onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
                      onBlur={(e) => (e.target.style.borderColor = "var(--border-light)")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      style={{
                        position: "absolute", top: "50%", right: 14, transform: "translateY(-50%)",
                        width: 20, height: 20, background: "none", border: "none", outline: "none",
                        padding: 0, cursor: "pointer", color: "var(--text-tertiary)",
                        display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0,
                      }}
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                {/* ── Remember me + Forgot password ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
                  <div
                    onClick={() => setRememberMe((v) => !v)}
                    style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      border: rememberMe ? "none" : "1.5px solid var(--border-medium)",
                      background: rememberMe ? "#818cf8" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background .15s, border-color .15s",
                    }}
                  >
                    {rememberMe && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                        <path d="M1 4l3 3 6-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setResetEmail(form.email); setError(""); }}
                  style={{ background: "none", border: "none", outline: "none", cursor: "pointer", color: "#818cf8", fontSize: 13, fontFamily: "inherit", padding: 0 }}
                >
                  Forgot password?
                </button>
                </div>

                {error && (
                  <div style={{ background: "rgba(248,113,113,.1)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#f87171" }}>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn btn-white btn-lg btn-full" disabled={loading}
                  style={{ marginTop: 4, opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </form>
            )}

            <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.6 }}>
              Don't have an account?{" "}
              <Link to="/signup" style={{ color: "#818cf8", textDecoration: "none" }}>Create one</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
